// packages/scene/src/board/lifecycle-style.ts
//
// Lifecycle paint, applied to a COMPOSED node rather than threaded through the
// eight shape composers (card / cylinder / table / state / class / enum /
// figure / boundary).
//
// FORM carries lifecycle; HUE stays with aspect and diff colouring. A dashed
// outline, reduced opacity, a struck title and a corner badge all compose with
// whatever colour the node already has, so team-colouring and a roadmap read at
// the same time instead of fighting for one channel.
//
// Both backends consume the result: the live board paints the same scene tree
// through `toHTML`, the export through `toSVG`.

import type { BoxNode, Node, Overlay, TextStyle } from "../model.js";
import type { BoardEdge, BoardItem, BoardSpace, EdgeWidgetStyle } from "./types.js";
import { boardItemKey } from "./types.js";
import { box, text } from "../model.js";
import { alpha, type Tokens } from "../tokens.js";

/** An element's state at a scrub point — mirrors the engine's `LifecycleState`. */
export type LifecycleState = "planned" | "live" | "deprecated" | "retired";

/** Resolved paint per node key — what `applyLifecycle` folds a whole space
 *  against, and what the live board looks each node up in. Note it carries an
 *  entry only for an element that is NOT plain-live, so a missing key means
 *  "live", not "unknown". */
export type LifecyclePaintMap = ReadonlyMap<string, LifecyclePaint>;

export interface LifecyclePaint {
  readonly state: LifecycleState;
  /** True when the element is `@deprecated` with a `from` still in the future —
   *  the sunset is announced but has not started. */
  readonly sunsetScheduled?: boolean;
  /** Badge text for the announced sunset / retirement (e.g. "SUNSET Q3"). */
  readonly badge?: string;
}

const OPACITY: Partial<Record<LifecycleState, number>> = {
  // Ghosted: agreed and dated, not built yet — visible in to-be.
  planned: 0.55,
  // Present but discouraged.
  deprecated: 0.55,
  // Should have been deleted — the faintest of the three, so it reads as
  // something on its way out rather than something merely not built yet.
  retired: 0.4,
};

/** Grey used for the deprecated/retired badge — deliberately NOT an accent, so
 *  it cannot be mistaken for aspect colour. */
const WARN = "#d6a052";

/**
 * Apply lifecycle paint to a composed board node.
 *
 * Returns the node unchanged for `live` with no scheduled sunset — the common
 * case pays nothing, and an undecorated board is byte-identical to before.
 */
export function applyLifecyclePaint(
  node: BoxNode | null,
  paint: LifecyclePaint | undefined,
  tokens: Tokens,
): BoxNode | null {
  if (!node || !paint) return node;
  if (paint.state === "live" && !paint.sunsetScheduled && !paint.badge) return node;

  let out: BoxNode = node;

  // 1. Opacity ONCE, on the root: a box's opacity fades everything inside it in
  //    both backends (CSS cascade in HTML, a wrapping group in SVG), so setting
  //    it per painting box would multiply the fade into itself wherever a card
  //    puts a filled header plate inside a filled body — 0.4 twice is 0.16.
  const opacity = OPACITY[paint.state];
  if (opacity !== undefined) {
    out = { ...out, style: { ...out.style, opacity } };
  }
  // 2. Dashing, though, has to reach every box that actually STROKES something.
  //    A cylinder, a figure and a C4 boundary draw their geometry BELOW the
  //    outer box — which itself often has no stroke at all, so dashing only the
  //    root would silently do nothing for those shapes.
  if (paint.state === "planned") {
    out = dashBoxes(out) as BoxNode;
  }

  // 3. Strike the title. The first text in reading order IS the title in every
  //    composer (card name, cylinder name, table caption, class name, …), so
  //    one walk covers all of them and cannot drift out of sync the way eight
  //    separate composer edits would.
  if (paint.state === "deprecated" || paint.state === "retired") {
    out = strikeFirstText(out) as BoxNode;
  }

  // 4. Corner badge for an announced sunset or a retired-but-still-written
  //    element — the lifecycle signal that survives with the scrubber parked,
  //    where the fade alone says little. Added AFTER the root opacity and laid
  //    out as a child, so it fades with the node rather than shouting over it —
  //    which is what the live board has always done, and now what the export
  //    does too.
  if (paint.badge) {
    out = {
      ...out,
      overlays: [...(out.overlays ?? []), badgeOverlay(paint.badge, tokens)],
    };
  }
  return out;
}

/**
 * Dash every box that actually strokes something, and every glyph.
 *
 * `dashed` only means anything alongside a stroke, so it is applied where a
 * stroke already exists rather than inventing one. Unlike opacity this does NOT
 * cascade in either backend, so it has to be walked in.
 */
function dashBoxes(node: Node): Node {
  // A glyph draws its own shape (cylinder, device figures) and is not a box —
  // without this a planned database would render identically to a live one.
  if (node.type === "glyph") return { ...node, dashed: true };
  if (node.type !== "box") return node;
  const s = node.style;
  const nextStyle = s?.stroke ? { ...s, dashed: true } : s;
  const children = node.children.map(dashBoxes);
  // OVERLAYS carry real content, not just chrome: the cylinder puts its whole
  // label stack in one, so skipping them would leave that shape undashed.
  const overlays = node.overlays?.map((o) => ({ ...o, node: dashBoxes(o.node) }));
  const changed =
    nextStyle !== s ||
    children.some((c, i) => c !== node.children[i]) ||
    !!overlays?.some((o, i) => o.node !== node.overlays?.[i]?.node);
  if (!changed) return node;
  return {
    ...node,
    ...(nextStyle ? { style: nextStyle } : {}),
    children,
    ...(overlays ? { overlays } : {}),
  };
}

/** Set `strike` on the first text-bearing node, depth-first in reading order. */
function strikeFirstText(node: Node): Node {
  if (node.type === "text") {
    const style: TextStyle = { ...node.style, strike: true };
    return { ...node, style };
  }
  // Richtext strike is per RUN, not per node style — setting the node style
  // would emit nothing.
  if (node.type === "richtext") {
    return { ...node, runs: node.runs.map((r) => ({ ...r, strike: true })) };
  }
  if (node.type !== "box") return node;
  const children = node.children;
  for (let i = 0; i < children.length; i++) {
    const before = children[i]!;
    const after = strikeFirstText(before);
    if (after !== before) {
      const next = [...children];
      next[i] = after;
      return { ...node, children: next };
    }
  }
  // Then overlays — the cylinder's title lives in one, so a children-only walk
  // would leave that shape's name unstruck.
  const overlays = node.overlays;
  if (overlays) {
    for (let i = 0; i < overlays.length; i++) {
      const before = overlays[i]!;
      const after = strikeFirstText(before.node);
      if (after !== before.node) {
        const next = [...overlays];
        next[i] = { ...before, node: after };
        return { ...node, overlays: next };
      }
    }
  }
  return node;
}

function badgeOverlay(label: string, t: Tokens): Overlay {
  const s = t.fontSize / 14;
  return {
    at: "top-left",
    node: box({
      direction: "row",
      align: "center",
      padding: [2 * s, t.space2, 2 * s, t.space2],
      reveal: "compact",
      style: {
        fill: alpha(WARN, 0.22),
        stroke: alpha(WARN, 0.5),
        strokeWidth: t.borderThin,
        radius: [t.radiusMd, 0, t.radiusSm, 0],
      },
      children: [
        text(label.toUpperCase(), {
          font: { family: t.fontFamily, size: t.fontSize * 0.62, weight: 700 },
          color: WARN,
          align: "center",
        }),
      ],
    }),
  };
}

// ---------------------------------------------------------------------------
// Edge lifecycle — derived from the edge's endpoints
// ---------------------------------------------------------------------------
//
// An edge exists only while BOTH of its endpoints do, so its state is the
// harsher of the two: an edge into a retired node is retired, an edge from a
// planned node is planned. Nothing declares this — a decoration never sits on a
// derived edge — so it is computed from the resolved node paint.
//
// Lives HERE, in scene, rather than in the shell, because it is expressed as an
// `EdgeWidgetStyle` overlay precisely so that BOTH backends can read it: the
// live board through `resolveEdgeStyle`, the export through `render-svg` (which
// already honours `widgetStyle.dashed` / `.opacity`). Keeping the fold in the
// shell is what left every exported edge unpainted while its endpoint nodes
// were painted correctly.

/** Harsher of two states — later in this order wins. */
const SEVERITY: readonly LifecycleState[] = ["live", "planned", "deprecated", "retired"];

function harsher(a: LifecycleState, b: LifecycleState): LifecycleState {
  return SEVERITY.indexOf(a) >= SEVERITY.indexOf(b) ? a : b;
}

const EDGE_OPACITY: Partial<Record<LifecycleState, number>> = {
  planned: 0.28,
  deprecated: 0.28,
  retired: 0.2,
};

/**
 * Fold the endpoints' lifecycle into an edge's resting style.
 *
 * Returns the ORIGINAL style object when neither endpoint carries lifecycle, so
 * an undecorated board allocates nothing and its edges stay byte-identical.
 */
export function edgeLifecycleStyle(
  paint: LifecyclePaintMap | null | undefined,
  fromKey: string | undefined,
  toKey: string | undefined,
  base: EdgeWidgetStyle | undefined,
): EdgeWidgetStyle | undefined {
  if (!paint || paint.size === 0) return base;
  const a = fromKey ? paint.get(fromKey)?.state : undefined;
  const b = toKey ? paint.get(toKey)?.state : undefined;
  if (!a && !b) return base;
  const state = harsher(a ?? "live", b ?? "live");
  const opacity = EDGE_OPACITY[state];
  if (opacity === undefined) return base;
  return {
    ...base,
    opacity: Math.min(base?.opacity ?? 1, opacity),
    // A planned edge is dashed like a planned node; a deprecated one keeps its
    // line shape and only fades, matching the node treatment.
    dashed: base?.dashed || state === "planned",
  };
}

/**
 * Fold lifecycle through a whole solved space — items, edges, ports and nested
 * spaces alike.
 *
 * This is the ONE place a scrub point meets a board. Node paint is stamped onto
 * `BoardItem.lifecycle` rather than left for the render site to look up, which
 * is what makes a folded space paint itself: `composeBoardNode` reads the item's
 * own paint, so `renderBoardSvg(foldedSpace)` is correct with no lifecycle
 * argument at all. Every backend that renders a folded space therefore paints
 * it, including ones written later — the HTTP board export was written without
 * the lookup and shipped every node as plain-live while the CLI and MCP renders
 * of the same workspace were correct.
 *
 * Edges keep going through `edgeLifecycleStyle` into the `EdgeWidgetStyle`
 * channel both backends already honour.
 *
 * Returns the space BY REFERENCE when there is no paint at all, so an
 * undecorated board allocates nothing and its export stays byte-identical.
 */
export function applyLifecycle(space: BoardSpace, paint: LifecyclePaintMap | null | undefined): BoardSpace {
  if (!paint || paint.size === 0) return space;

  const foldEdge = (e: BoardEdge): BoardEdge => {
    const d = e.details[0];
    const style = edgeLifecycleStyle(paint, d?.fromPath, d?.toPath, e.widgetStyle);
    return style === e.widgetStyle ? e : { ...e, widgetStyle: style };
  };

  const foldItem = (it: BoardItem): BoardItem => {
    // A cross-space call is drawn as an external PORT, not an edge — style it
    // through the same fold or a boundary-crossing call to a retired module
    // stays fully live-looking in the export.
    const ports = it.externalPorts?.map((p) => {
      const d = p.details[0];
      const style = edgeLifecycleStyle(paint, d?.fromPath, d?.toPath, p.widgetStyle);
      return style === p.widgetStyle ? p : { ...p, widgetStyle: style };
    });
    const inner = it.space ? applyLifecycle(it.space, paint) : it.space;
    const own = paint.get(boardItemKey(it));
    const portsChanged = !!ports && ports.some((p, i) => p !== it.externalPorts![i]);
    if (inner === it.space && !portsChanged && own === it.lifecycle) return it;
    return {
      ...it,
      // UNCONDITIONAL, so a re-fold at a scrub point where this element is live
      // CLEARS the previous point's paint instead of carrying it forward on the
      // spread. A live element is absent from the paint map (see
      // `resolveLifecyclePaint`), so a conditional stamp can only ever add — and
      // stale paint is not a missing mark but a wrong one. The guard above
      // already returns the item by reference when nothing changed, undefined
      // included, so this costs an unpainted board nothing.
      lifecycle: own,
      ...(ports ? { externalPorts: ports } : {}),
      ...(inner ? { space: inner } : {}),
    };
  };

  return {
    ...space,
    items: space.items.map(foldItem),
    ...(space.edges ? { edges: space.edges.map(foldEdge) } : {}),
  };
}
